<template>
	<n-breadcrumb
		class="breadcrumb [&_.n-breadcrumb-item\_\_link]:text-default! [&_.n-breadcrumb-item\_\_link]:text-lg"
	>
		<n-breadcrumb-item @click="goto({ path: '/' })">
			<Icon name="carbon:dashboard" :size="20" class="text-default!" />
		</n-breadcrumb-item>
		<TransitionGroup name="anim">
			<n-breadcrumb-item
				v-for="(item, index) of items"
				:key="item.key"
				:class="`index-${index} ${index < items.length ? 'font-bold' : ''}`"
				@click="goto({ path: item.path })"
			>
				{{ item.name }}
			</n-breadcrumb-item>
		</TransitionGroup>
	</n-breadcrumb>
</template>

<script lang="ts" setup>
import type { RouteLocationNormalizedLoaded } from "vue-router"
import type { BreadcrumbItem } from "@/composables/common/useBreadcrumb"
import type { I18nString } from "@/lang/config"
import _compact from "lodash/compact"
import _isEqual from "lodash/isEqual"
import _snakeCase from "lodash/snakeCase"
import _split from "lodash/split"
import _take from "lodash/take"
import { NBreadcrumb, NBreadcrumbItem } from "naive-ui"
import { onBeforeMount } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute, useRouter } from "vue-router"
import Icon from "@/components/common/Icon.vue"
import { useBreadcrumb } from "@/composables/common/useBreadcrumb"
import { getParamId, getParamLabel } from "@/utils"

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const { items, setItems } = useBreadcrumb()

function goto(page: Partial<BreadcrumbItem>) {
	if (page.name && page.name !== route.name) {
		router.push({ name: page.name })
	}
	if (page.path && page.path !== route.path) {
		router.push({ path: page.path })
	}
}

function checkRoute(route: RouteLocationNormalizedLoaded) {
	const newItems: BreadcrumbItem[] = []
	const url = route?.path || route?.matched?.[0]?.aliasOf?.path || ""
	const pathChunks = _compact(_split(url, "/"))

	let cumulativePath = ""

	for (const key in pathChunks) {
		const chunk = pathChunks[key] || ""

		cumulativePath += `/${chunk}`

		// questo switch salta tutte le "liste"
		// da così: /Luisa Bianchi/Terapie/Dettaglio terapia
		// a così: /Luisa Bianchi/Dettaglio terapia
		switch (_snakeCase(chunk)) {
			case "patients":
			case "visits":
			case "exams":
			case "pregnancies":
			case "therapies":
				if (pathChunks.at(-1) !== chunk) {
					continue
				}
		}

		let name = getParamLabel(chunk)
		// questo switch gestisce casistiche come: /patients/15/exams/create
		// in cui dopo /patients c'è solo l'ID
		// è possibile anche gestire casi in cui dopo /patient è presente "create"
		switch (_snakeCase(pathChunks[Number.parseInt(key) - 1])) {
			case "patients":
				if (name === `${getParamId(chunk)}`)
					name = `${t<I18nString>(`common.misc.patient`)} #${getParamId(chunk)}`
				break
			case "visits":
				if (name === `${getParamId(chunk)}`)
					name = `${t<I18nString>(`common.misc.visit`)} #${getParamId(chunk)}`
				break
			case "exams":
				if (name === `${getParamId(chunk)}`) name = `${t<I18nString>(`common.misc.exam`)} #${getParamId(chunk)}`
				break
			default:
				name = t(`navigation.titles.${_snakeCase(chunk)}`, getParamLabel(chunk))
				break
		}

		newItems.push({
			name,
			path: cumulativePath,
			key: name + cumulativePath
		})
	}

	// se esiste un titolo nella rotta corrente allora viene attribuito all'ultimo chunk che corrisponde ovviamente al path corrente
	if (route.meta?.title && newItems.length) {
		const lastItem = newItems.at(-1)
		if (lastItem) {
			lastItem.name = t(`navigation.titles.${String(route.meta.title)}`, String(route.meta.title))
		}
	}

	if (!_isEqual(items.value, newItems)) {
		setItems(_take(newItems, 10))
	}
}

onBeforeMount(() => {
	checkRoute(router.currentRoute.value)

	router.beforeResolve(route => {
		checkRoute(route)
	})
})
</script>

<style lang="scss" scoped>
.breadcrumb {
	.anim-move,
	.anim-enter-active {
		transition: all 0.5s var(--bezier-ease);

		@for $i from 0 through 10 {
			&.index-#{$i} {
				transition-delay: $i * 0.1s;
			}
		}
	}

	.anim-leave-active {
		display: none;
	}

	.anim-enter-from {
		opacity: 0;
		transform: translateX(-5px);
	}
}
</style>
